/**
 * Named icon components — optional compatibility layer.
 *
 * These let existing call sites that used `<CloseIcon />` or `<EditIcon />`
 * continue working with a simple import swap:
 *
 *   - import { Close } from '@mui/icons-material';
 *   + import { Close } from '../components/icons';
 *
 * Each wrapper forwards all props (including ref) to `SvgIcon` with the
 * correct `name` pre-filled.
 *
 * ## Should you generate these for all icons?
 *
 * For a migration of 50–150 icons across many files, yes — these wrappers
 * make the diff at each call site a one-line import change. A find-and-replace
 * regex can handle 90% of the migration automatically:
 *
 *   Find:    import \{ (.+) \} from ['"]@mui/icons-material['"];?
 *   Replace: import { $1 } from '../components/icons';
 *
 * The wrappers cost virtually nothing at runtime (one extra component frame)
 * and keep call-site churn minimal.
 */
import React from "react";
import { SvgIcon, SvgIconProps } from "./SvgIcon";

type NamedIconProps = Omit<SvgIconProps, "name">;

function createNamedIcon(name: SvgIconProps["name"], displayName: string) {
  const Icon = React.forwardRef<SVGSVGElement, NamedIconProps>((props, ref) => (
    <SvgIcon ref={ref} name={name} {...props} />
  ));
  Icon.displayName = displayName;
  return Icon;
}

// ─── Named exports ────────────────────────────────────────────────────────────
// Sorted alphabetically. The export name matches the MUI import name so
// migration is a simple import-path swap.

export const Accessibility = createNamedIcon("accessibility", "AccessibilityIcon");
export const AccessTime = createNamedIcon("accessTime", "AccessTimeIcon");
export const AccountCircle = createNamedIcon("accountCircle", "AccountCircleIcon");
export const Add = createNamedIcon("add", "AddIcon");
export const AddBox = createNamedIcon("addBox", "AddBoxIcon");
export const AddCircle = createNamedIcon("addCircle", "AddCircleIcon");
export const AddComment = createNamedIcon("addComment", "AddCommentIcon");
export const AirlinesRounded = createNamedIcon("airlinesRounded", "AirlinesRoundedIcon");
export const AirplanemodeInactive = createNamedIcon("airplanemodeInactive", "AirplanemodeInactiveIcon");
export const AlarmOn = createNamedIcon("alarmOn", "AlarmOnIcon");
export const AllInclusive = createNamedIcon("allInclusive", "AllInclusiveIcon");
export const Announcement = createNamedIcon("announcement", "AnnouncementIcon");
export const ArrowDownward = createNamedIcon("arrowDownward", "ArrowDownwardIcon");
export const ArrowDropDown = createNamedIcon("arrowDropDown", "ArrowDropDownIcon");
export const ArrowUpward = createNamedIcon("arrowUpward", "ArrowUpwardIcon");
export const Attachment = createNamedIcon("attachment", "AttachmentIcon");
export const AttachMoney = createNamedIcon("attachMoney", "AttachMoneyIcon");
export const Backspace = createNamedIcon("backspace", "BackspaceIcon");
export const Build = createNamedIcon("build", "BuildIcon");
export const Chat = createNamedIcon("chat", "ChatIcon");
export const Check = createNamedIcon("check", "CheckIcon");
export const CheckBox = createNamedIcon("checkBox", "CheckBoxIcon");
export const CheckBoxOutlineBlank = createNamedIcon("checkBoxOutlineBlank", "CheckBoxOutlineBlankIcon");
export const CheckCircle = createNamedIcon("checkCircle", "CheckCircleIcon");
export const CheckCircleOutline = createNamedIcon("checkCircleOutline", "CheckCircleOutlineIcon");
export const CheckCircleRounded = createNamedIcon("checkCircleRounded", "CheckCircleRoundedIcon");
export const ChecklistRtl = createNamedIcon("checklistRtl", "ChecklistRtlIcon");
export const Clear = createNamedIcon("clear", "ClearIcon");
export const Close = createNamedIcon("close", "CloseIcon");
export const CloseOutlined = createNamedIcon("closeOutlined", "CloseOutlinedIcon");
export const CloudDownload = createNamedIcon("cloudDownload", "CloudDownloadIcon");
export const Comment = createNamedIcon("comment", "CommentIcon");
export const Delete = createNamedIcon("delete", "DeleteIcon");
export const Description = createNamedIcon("description", "DescriptionIcon");
export const DirectionsWalk = createNamedIcon("directionsWalk", "DirectionsWalkIcon");
export const Download = createNamedIcon("download", "DownloadIcon");
export const Edit = createNamedIcon("edit", "EditIcon");
export const ErrorOutline = createNamedIcon("errorOutline", "ErrorOutlineIcon");
export const FlightTakeoff = createNamedIcon("flightTakeoff", "FlightTakeoffIcon");
export const FolderDelete = createNamedIcon("folderDelete", "FolderDeleteIcon");
export const FormatListNumbered = createNamedIcon("formatListNumbered", "FormatListNumberedIcon");
export const Fullscreen = createNamedIcon("fullscreen", "FullscreenIcon");
export const FullscreenExit = createNamedIcon("fullscreenExit", "FullscreenExitIcon");
export const Groups = createNamedIcon("groups", "GroupsIcon");
export const HighlightOffRounded = createNamedIcon("highlightOffRounded", "HighlightOffRoundedIcon");
export const History = createNamedIcon("history", "HistoryIcon");
export const HourglassFull = createNamedIcon("hourglassFull", "HourglassFullIcon");
export const IndeterminateCheckBox = createNamedIcon("indeterminateCheckBox", "IndeterminateCheckBoxIcon");
export const Info = createNamedIcon("info", "InfoIcon");
export const InfoOutlined = createNamedIcon("infoOutlined", "InfoOutlinedIcon");
export const KeyboardArrowLeft = createNamedIcon("keyboardArrowLeft", "KeyboardArrowLeftIcon");
export const KeyboardArrowRight = createNamedIcon("keyboardArrowRight", "KeyboardArrowRightIcon");
export const KeyboardDoubleArrowDown = createNamedIcon("keyboardDoubleArrowDown", "KeyboardDoubleArrowDownIcon");
export const KeyboardDoubleArrowUp = createNamedIcon("keyboardDoubleArrowUp", "KeyboardDoubleArrowUpIcon");
export const Link = createNamedIcon("link", "LinkIcon");
export const LocalAirport = createNamedIcon("localAirport", "LocalAirportIcon");
export const Lock = createNamedIcon("lock", "LockIcon");
export const Menu = createNamedIcon("menu", "MenuIcon");
export const MenuBook = createNamedIcon("menuBook", "MenuBookIcon");
export const NewReleases = createNamedIcon("newReleases", "NewReleasesIcon");
export const NightlightRound = createNamedIcon("nightlightRound", "NightlightRoundIcon");
export const NotInterested = createNamedIcon("notInterested", "NotInterestedIcon");
export const Notifications = createNamedIcon("notifications", "NotificationsIcon");
export const NotificationsActive = createNamedIcon("notificationsActive", "NotificationsActiveIcon");
export const Person = createNamedIcon("person", "PersonIcon");
export const PhoneAndroidOutlined = createNamedIcon("phoneAndroidOutlined", "PhoneAndroidOutlinedIcon");
export const Print = createNamedIcon("print", "PrintIcon");
export const PriorityHigh = createNamedIcon("priorityHigh", "PriorityHighIcon");
export const Public = createNamedIcon("public", "PublicIcon");
export const RemoveCircle = createNamedIcon("removeCircle", "RemoveCircleIcon");
export const ReportProblemOutlined = createNamedIcon("reportProblemOutlined", "ReportProblemOutlinedIcon");
export const Save = createNamedIcon("save", "SaveIcon");
export const ScheduleOutlined = createNamedIcon("scheduleOutlined", "ScheduleOutlinedIcon");
export const School = createNamedIcon("school", "SchoolIcon");
export const Search = createNamedIcon("search", "SearchIcon");
export const Send = createNamedIcon("send", "SendIcon");
export const ShoppingCart = createNamedIcon("shoppingCart", "ShoppingCartIcon");
export const Soap = createNamedIcon("soap", "SoapIcon");
export const Stop = createNamedIcon("stop", "StopIcon");
export const StopCircle = createNamedIcon("stopCircle", "StopCircleIcon");
export const Title = createNamedIcon("title", "TitleIcon");
export const Upload = createNamedIcon("upload", "UploadIcon");
export const Visibility = createNamedIcon("visibility", "VisibilityIcon");
export const Work = createNamedIcon("work", "WorkIcon");

// ─── Default-import aliases ───────────────────────────────────────────────────
// MUI icons are also imported via default imports:
//   import AddIcon from '@mui/icons-material/Add';
// The named exports above already support this pattern when destructured, but
// some call sites use:
//   import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
// These are satisfied by the named exports above — just change the import path:
//   import { NotificationsActive as NotificationsActiveIcon } from '../components/icons';

// ─── Re-export for convenience ────────────────────────────────────────────────
// Some MUI imports alias on import, e.g.:
//   import { CheckBox as CheckedIcon, CheckBoxOutlineBlank as UncheckedIcon, IndeterminateCheckBox as IndeterminateIcon } from '@mui/icons-material';
// After migration:
//   import { CheckBox as CheckedIcon, CheckBoxOutlineBlank as UncheckedIcon, IndeterminateCheckBox as IndeterminateIcon } from '../components/icons';
